import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemToken } from 'designSystem/types';
import * as S from './styles';

interface TypeScaleProps {
	tokens: DesignSystemToken[];
	previewText: string;
	dark: boolean;
	primaryFont: string;
}

const getSizePx = (token: DesignSystemToken): number => {
	if (token.value.kind === 'fontSize') return token.value.length.px ?? 16;
	if (token.value.kind === 'length') return token.value.px ?? 16;
	return 16;
};

const TypeScale = ({ tokens, previewText, dark, primaryFont }: TypeScaleProps) => {
	const dispatch = useDispatch();
	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const sorted = useMemo(() => [...tokens].sort((a, b) => getSizePx(b) - getSizePx(a)), [tokens]);

	const isSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	if (sorted.length === 0) return null;

	return (
		<S.TypeScaleList>
			{sorted.map((token) => {
				const px = getSizePx(token);
				return (
					<S.TypeScaleRow
						key={token.id}
						type="button"
						aria-pressed={isSelected(token)}
						$selected={isSelected(token)}
						$dark={dark}
						onClick={() =>
							dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }))
						}
					>
						<S.TypeScaleTooltip>{token.cssVariable}</S.TypeScaleTooltip>
						<S.TypeScaleLabel>{token.name}</S.TypeScaleLabel>
						<S.TypeScaleSample
							$dark={dark}
							style={{ fontSize: `${px}px`, fontFamily: primaryFont }}
						>
							{previewText}
						</S.TypeScaleSample>
						<S.TypeScaleValue>{px}px</S.TypeScaleValue>
					</S.TypeScaleRow>
				);
			})}
		</S.TypeScaleList>
	);
};

export default TypeScale;
